import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";
import ConfirmDialog from "../ConfirmDialog";

import ProjectService from "../../services/ProjectService";
import { Mode } from "./StatePage";
import { StateModel } from "../../models/StateModel";
import { getAllStates } from "../../redux/actions/StateActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { createAutocompleteOptions } from "../../utility/utils";

const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  code: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: StateModel;
};

const StateForm = ({ mode, handleModalClose, data }: Props) => {
  const initialValues = useMemo(
    () => ({
      name: "",
      code: "",
      country: "",
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();

  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const allCountries = useAppSelector((state) => state.country.all_Countries);
  const countryOptions = useMemo(() => {
    return createAutocompleteOptions(
      allCountries,
      "countryCode",
      "countryName"
    );
  }, [allCountries]);

  useEffect(() => {
    if (mode === "delete" && data) {
      setFormState(() => ({
        name: data.stateName!,
        code: data.stateCode!,
        country: data.countryCode!,
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: StateModel = {
        stateName: "",
        stateCode: "",
        countryCode: "",
      };
      if (mode === "add") {
        formData.stateName = values.name;
        formData.stateCode = values.code;
        formData.countryCode = values.country;
      } else if (data) {
        formData = {
          ...data,
          stateName: values.name,
          stateCode: values.code,
          countryCode: values.country,
        };
      }
      mode === "delete"
        ? await ProjectService.deleteState(formData)
        : await ProjectService.createState(formData, data?.stateCode);
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(getAllStates());
      displayToast(
        `State ${mode === "add" ? "created" : "deleted"} successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => (
        <>
          <Form>
            {mode === "delete" ? (
              <ConfirmDialog handleModalClose={handleModalClose} />
            ) : (
              <Stack gap={2}>
                <FormikControl
                  control="input"
                  type="text"
                  name="name"
                  size="small"
                  label="State Name"
                  placeholder="Enter Name"
                />
                <FormikControl
                  control="input"
                  type="text"
                  name="code"
                  size="small"
                  label="State Code"
                  placeholder="Enter Code"
                />
                <FormikControl
                  control="autocomplete"
                  options={countryOptions}
                  name="country"
                  size="small"
                  label="Country"
                  placeholder="Select Country"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
            )}
          </Form>
          {formik.isSubmitting && <Loader />}
        </>
      )}
    </Formik>
  );
};

export default StateForm;
